// ---------------------------------------------------------------------------
// "Worth a stop" — the community detour layer (Morgan, Aug 4).
//
// Users nominate places worth stopping for, and vote 👍/👎 on ANY option the
// detours panel shows — their own nominations and OSM-discovered basics
// alike. Voting on an OSM place PROMOTES it into nominated_stops (keyed by
// osm_ref), so one table accumulates the community's verdict on every stop.
// Community votes are the quality signal that Google's star ratings used to
// provide — except they're ours, free, and about pleasantness rather than
// commerce.
//
// Display rules (the panel applies these):
//   · nominated/endorsed stops rank above OSM basics ("Local pick" badge)
//   · CONSENSUS BURIES: ≥3 downvotes AND downvotes > 2× upvotes → hidden
//     from the panel (still visible to Morgan; her reject is the hard tool)
//   · one vote per person per stop, changeable, removable
// ---------------------------------------------------------------------------
import { supabase } from "@/integrations/supabase/client";
import { getEffectiveUserId } from "@/utils/anonId";
import { bbox, type LngLat } from "@/utils/polyline";

export interface NominatedStop {
  id: string;
  name: string;
  why: string | null;
  lat: number;
  lng: number;
  /** 'user' = someone nominated it outright; 'osm' = promoted by a vote. */
  source: string;
  osmRef: string | null;
  up: number;
  down: number;
  /** The current user's vote: 1, -1, or 0 (none). */
  myVote: number;
}

/** ≥3 downvotes and downvotes > 2× upvotes → consensus says skip it. */
export function buriedByConsensus(up: number, down: number): boolean {
  return down >= 3 && down > up * 2;
}

/** All approved stops inside the route's bounding box, with vote tallies. */
export async function fetchStopsAlongRoute(coordinates: LngLat[]): Promise<NominatedStop[]> {
  if (coordinates.length < 2) return [];
  const box = bbox(coordinates, 0.03);
  const { data: rows, error } = await supabase
    .from("nominated_stops")
    .select("id,name,why,lat,lng,source,osm_ref")
    .gte("lat", box.minLat).lte("lat", box.maxLat)
    .gte("lng", box.minLng).lte("lng", box.maxLng)
    .limit(200);
  if (error || !rows || rows.length === 0) return [];

  const ids = rows.map((r) => r.id);
  const { data: votes } = await supabase
    .from("nominated_stop_votes")
    .select("stop_id,user_id,value")
    .in("stop_id", ids);
  const me = await getEffectiveUserId().catch(() => "");

  const tally = new Map<string, { up: number; down: number; mine: number }>();
  for (const v of votes ?? []) {
    const t = tally.get(v.stop_id) ?? { up: 0, down: 0, mine: 0 };
    if (v.value > 0) t.up++; else t.down++;
    if (v.user_id === me) t.mine = v.value;
    tally.set(v.stop_id, t);
  }

  return rows.map((r) => {
    const t = tally.get(r.id) ?? { up: 0, down: 0, mine: 0 };
    return {
      id: r.id, name: r.name, why: r.why, lat: r.lat, lng: r.lng,
      source: r.source, osmRef: r.osm_ref, up: t.up, down: t.down, myVote: t.mine,
    };
  });
}

/** Nominate a brand-new stop. Caller gates on auth (same as tagging). */
export async function nominateStop(input: {
  name: string; why?: string; lat: number; lng: number;
}): Promise<{ ok: boolean; error?: string }> {
  const userId = await getEffectiveUserId();
  const { error } = await supabase.from("nominated_stops").insert({
    user_id: userId,
    name: input.name.trim().slice(0, 120),
    why: input.why?.trim() ? input.why.trim().slice(0, 280) : null,
    lat: input.lat,
    lng: input.lng,
  });
  return error ? { ok: false, error: error.message } : { ok: true };
}

/** Cast/change/remove a vote on an existing stop. value 0 removes. */
export async function voteStop(stopId: string, value: 1 | -1 | 0): Promise<void> {
  const userId = await getEffectiveUserId();
  if (value === 0) {
    await supabase.from("nominated_stop_votes")
      .delete().eq("stop_id", stopId).eq("user_id", userId);
    return;
  }
  await supabase.from("nominated_stop_votes").upsert(
    { stop_id: stopId, user_id: userId, value },
    { onConflict: "stop_id,user_id" },
  );
}

/**
 * Vote on an OSM-discovered panel option. First vote promotes the place into
 * nominated_stops (source 'osm'); subsequent votes find it by osm_ref.
 */
export async function voteOsmPlace(
  place: { id: string; name: string; center: LngLat },
  value: 1 | -1,
): Promise<{ stopId: string | null }> {
  const userId = await getEffectiveUserId();
  // Try to find an existing promotion.
  const { data: existing } = await supabase
    .from("nominated_stops")
    .select("id")
    .eq("osm_ref", place.id)
    .maybeSingle();
  let stopId = existing?.id ?? null;
  if (!stopId) {
    const { data: inserted, error } = await supabase
      .from("nominated_stops")
      .insert({
        user_id: userId,
        name: place.name.slice(0, 120),
        lat: place.center[1],
        lng: place.center[0],
        source: "osm",
        osm_ref: place.id,
      })
      .select("id")
      .maybeSingle();
    if (error) {
      // Unique-index race: someone else promoted it first — fetch theirs.
      const { data: again } = await supabase
        .from("nominated_stops").select("id").eq("osm_ref", place.id).maybeSingle();
      stopId = again?.id ?? null;
    } else {
      stopId = inserted?.id ?? null;
    }
  }
  if (stopId) await voteStop(stopId, value);
  return { stopId };
}
